import { create } from "zustand";

export type ConsultDeleteTarget = {
  id: string;
  title: string;
};

/** 診察 ID をキーにした選択状態（@tanstack/react-table の RowSelectionState と同じ形） */
export type ConsultRowSelection = Record<string, boolean>;

type ConsultRowSelectionUpdater =
  | ConsultRowSelection
  | ((prev: ConsultRowSelection) => ConsultRowSelection);

export interface ConsultDeleteSelectionData {
  rowSelection: ConsultRowSelection;
  isDialogOpen: boolean;
  deleteTargets: ConsultDeleteTarget[];
}

export interface ConsultDeleteSelectionState
  extends ConsultDeleteSelectionData {
  setRowSelection: (updater: ConsultRowSelectionUpdater) => void;
  keepOnlyListedSelection: (listedConsultIds: readonly string[]) => void;
  openDeleteDialog: (targets: ConsultDeleteTarget[]) => void;
  closeDeleteDialog: () => void;
  reset: () => void;
}

const initialState: ConsultDeleteSelectionData = {
  rowSelection: {},
  isDialogOpen: false,
  deleteTargets: [],
};

/**
 * 診察一覧の選択削除の状態。
 * 一覧は PC/SP のレイアウトごとに別々にマウントされ、ブレークポイントをまたぐと作り直される。
 * 選択と削除モーダルを一覧の外で持つことで、端末の向きや幅を変えても引き継げるようにする。
 * 検索語やページは一覧ごとに異なり得るため、選択は行番号ではなく診察 ID で持つ。
 */
export const useConsultDeleteSelection = create<ConsultDeleteSelectionState>(
  (set) => ({
    ...initialState,

    setRowSelection: (updater) =>
      set((state) => ({
        rowSelection:
          typeof updater === "function" ? updater(state.rowSelection) : updater,
      })),

    // 一覧に出ていない診察は外す。残すと件数表示と実際の選択がずれ、
    // チェックを外す手段も無いままページ送りが無効になる
    keepOnlyListedSelection: (listedConsultIds) =>
      set((state) => {
        const listed = new Set(listedConsultIds);
        const selectedIds = Object.keys(state.rowSelection);
        const keptIds = selectedIds.filter((id) => listed.has(id));
        if (keptIds.length === selectedIds.length) return state;
        return {
          rowSelection: Object.fromEntries(
            keptIds.map((id) => [id, state.rowSelection[id] ?? false]),
          ),
        };
      }),

    openDeleteDialog: (deleteTargets) =>
      set({ isDialogOpen: true, deleteTargets }),

    // 閉じるアニメーション中もタイトル一覧を出し続けるため、削除対象は次に開くまで残す
    closeDeleteDialog: () => set({ isDialogOpen: false }),

    reset: () => set(initialState),
  }),
);
